import {
  homepageEditorSections,
  liveV21HomepageBaseline,
  type HomepageContent,
  type HomepageRevisionSummary,
} from "@whitehaven/contracts/homepage";

export type HomepageEditorSectionId =
  (typeof homepageEditorSections)[number]["id"];

export interface HomepageEditorSectionDefinition {
  readonly id: HomepageEditorSectionId;
  readonly path: (typeof homepageEditorSections)[number]["path"];
  readonly label: string;
  readonly description: string;
}

const sectionPresentation: Readonly<
  Record<
    HomepageEditorSectionId,
    Pick<HomepageEditorSectionDefinition, "label" | "description">
  >
> = {
  seo: {
    label: "Search and sharing",
    description: "Browser title and search-result description.",
  },
  theme: {
    label: "Theme",
    description:
      "Approved palette, typography, spacing, button, and card treatments.",
  },
  hero: {
    label: "Hero",
    description: "Opening headline, visit path, actions, and trust messages.",
  },
  services: {
    label: "Services",
    description: "Introduction above live Service catalog cards.",
  },
  bookingSteps: {
    label: "Booking steps",
    description: "The three-step new-visitor booking explanation.",
  },
  packages: {
    label: "Packages",
    description: "Introduction above live Package catalog cards.",
  },
  careBoundary: {
    label: "Wellness vs. clinical",
    description: "The two-column wellness and clinical-care boundary.",
  },
  guides: {
    label: "Visit guides",
    description: "Three answer-first guide cards.",
  },
  faq: {
    label: "Frequently asked questions",
    description: "FAQ introduction and five public answers.",
  },
  contact: {
    label: "Contact",
    description: "Contact invitation, notices, and inquiry labels.",
  },
  addedSections: {
    label: "Section library",
    description:
      "Reusable content blocks added from safe, responsive templates.",
  },
  footer: {
    label: "Footer",
    description: "Homepage footer description and wellness notice.",
  },
};

export const homepageEditorSectionDefinitions = homepageEditorSections.map(
  (section) => ({ ...section, ...sectionPresentation[section.id] }),
) satisfies readonly HomepageEditorSectionDefinition[];

export function cloneHomepageContent(
  content: HomepageContent,
): HomepageContent {
  return structuredClone(content);
}

export function homepageDraftChanged(
  draft: HomepageContent,
  persisted: HomepageContent,
): boolean {
  return JSON.stringify(draft) !== JSON.stringify(persisted);
}

export function createBlankHomepageContent(): HomepageContent {
  return cloneHomepageContent(liveV21HomepageBaseline);
}

export function revisionEditorLabel(revision: HomepageRevisionSummary): string {
  return revision.editor.emailNormalized ?? revision.editor.userId;
}

export function formatHomepageRevisionDate(
  timestamp: string,
  locale = "en-US",
): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Invalid timestamp";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Pacific/Honolulu",
    timeZoneName: "short",
  }).format(date);
}
